import { isArray } from "lodash";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useReactFlow } from "reactflow";
import { Dimmer, Grid, Loader } from "semantic-ui-react";
import {
  useGetAllFacilities,
  useGetFarmGateProcessDataById,
  useGetProcessTypesWholeList,
  useGetTransportTypesList
} from "../../../../../api/cultivations";
import { useGetFacilityHeatSources } from "../../../../../api/facility";
import {
  useDeleteSimulationFarmGateProcessData,
  useGetSimulationFarmGateProcessDataById,
  usePostSimulationFarmGateProcessData,
  usePutSimulationFarmGateProcessData
} from "../../../../../api/simulation/farmGateProcess";
import {
  useGetChemicalTypes,
  useGetCipSipChemicalTypes,
  useGetHeatSourceType,
  useGetWasteManagementWasteTypes
} from "../../../../../api/static-data";
import { SimulationChemicalContent } from "../../../../../components";
import ConfirmModal from "../../../../../components/confirmViewModal/ConfirmModal";
import { DrawingContext } from "../../../../../components/drawing-tool/DrawingTool";
import MainBottomButtonView from "../../../../../components/mainBottomButtonView/MainBottomButtonView";
import SimulationMeasurementDetails from "../../../../../components/measurementDetails/SimulationMeasurementDetails";
import SimulationProcessingDetails from "../../../../../components/processingDetails/SimulationProcessingDetails";
import { SimulationWasteManagementContent } from "../../../../../components/wasteManagment/SimulationWasteManagementModal";
import { dataTypeOptions, SipContents } from "../../../../../config/constants";
import { DEFAULT_HEAT_SOURCE } from "../../../../../config/drawingConstants";
import { FacilityType } from "../../../../../config/enums/FacilityType.enum";
import { IngredientDataTypesOption } from "../../../../../config/enums/IngredientType.enum";
import { MEASUREMENT_TYPES } from "../../../../../config/enums/MeasurementTypes";
import {
  AccordionTypes,
  validateAccordionOpen,
  validateAccordionSubmit
} from "../../../../../helpers/AccordionHelper";
import { errorView, successMessage } from "../../../../../helpers/ErrorHandler";
import useWindowDimensions from "../../../../../hooks/windowsSize";
import { removeNewDrawingItemStatus, saveNewDrawingItem } from "../../../../../utils/cacheStorage";
import AddIngredientModal from "../../../../cultivation/processing/IngredientContentModal";
import SimulationProcessHeatSource from "../../../../cultivation/processing/SimulationProcessHeatSource";
import SimulationRecipeContent from "../../../../cultivation/processing/SimulationRecipeModal";
import SimulationSipContentContent from "../../../../cultivation/processing/SimulationSipContentModal";
import FacilitySimulationEditModal from "../../../../facility/facilitySimulation/FacilitySimulationEditModal";

interface HandleValidation {
  checkCustomRequired(data?: any): void;
}

interface ProcessFormData {
  processingName: string;
  processingType: number;
  facilityName: string;
  dataType: number;
  machineModelNo: string;
  capacity: number;
  capacityUnit: number;
  input: number;
  inputUnit: number;
  output: number;
  outputUnit: number;
  sideStream: number;
  sideStreamUnit: number;
  heat: number;
  heatUnit: number;
  electricity: number;
  electricityUnit: number;
  water: number;
  waterUnit: number;
  citricAcid?: number;
}

interface HeatSourceData {
  HeatSource: number;
  HeatAmount: number;
}

export const FarmGateProcessBase = ({ modalData }: any) => {
  const { farmGateId, ingredientId, supplierId, nodeItem } = modalData;
  const { deleteElements } = useReactFlow();
  const { chartEdges, chartNodes, setNodeItem, setChartNodes, saveDrawing } =
    useContext(DrawingContext);
  const [processId, setProcessId] = useState<string>(nodeItem?.data?.reference?.processId);
  const { height } = useWindowDimensions();
  const [viewIngredient, setViewIngredient] = useState(false);
  const [facilityName, setFacilityName] = useState<string>("");
  const [processingType, setProcessingType] = useState("");
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
  const [bounceEnable, setBounceEnable] = useState(false);
  const [cipSipData, setCipSipData] = useState<any>([]);
  const [addIngredientActive, setAddIngredientActive] = useState(false);
  const [accordionStatus, setAccordionStatus] = useState<boolean>(false);
  const [accordionType, setAccordionType] = useState<AccordionTypes | null>(null);
  const customCapacityValidation = useRef<HandleValidation>(null);
  const customInputValidation = useRef<HandleValidation>(null);
  const customOutputValidation = useRef<HandleValidation>(null);
  const [heatSourceTable, setHeatSourceTable] = useState<any>([]);
  const [defaultHeatSource, setDefaultHeatSource] = useState<number>(DEFAULT_HEAT_SOURCE);
  const [dataType, setDataType] = useState<string | undefined>(IngredientDataTypesOption.SPECIFIC);
  const [visibleFacilityModal, setVisibleFacilityModal] = useState<boolean>(false);
  const [unitTypes, setUnitTypes] = useState<UnitTypes>({
    capacityUnit: MEASUREMENT_TYPES.WEIGHT_KG,
    inputUnit: MEASUREMENT_TYPES.WEIGHT_KG,
    outputUnit: MEASUREMENT_TYPES.WEIGHT_KG,
    sideStreamUnit: MEASUREMENT_TYPES.WEIGHT_KG,
    heatUnit: MEASUREMENT_TYPES.ENERGY_MJ_KG,
    electricityUnit: MEASUREMENT_TYPES.POWER_kWh_KG,
    waterUnit: MEASUREMENT_TYPES.VOLUME_m3_PER_KG
  });

  const { data: facilityWholeList, isSuccess: isFacilitySuccess } = useGetAllFacilities(
    FacilityType.FACTORY
  );
  const { data: processTypesWholeList } = useGetProcessTypesWholeList(dataType);
  const { mutate: postFarmGateProcessData, isLoading: isCreateProcessLoading } =
    usePostSimulationFarmGateProcessData();
  const { mutate: putFarmGateProcessData, isLoading: isUpdateProcessLoading } =
    usePutSimulationFarmGateProcessData();
  const { mutate: deleteFarmGateProcessData, isLoading: isDeleteProcessLoading } =
    useDeleteSimulationFarmGateProcessData();

  const { data, refetch, isLoading } = useGetSimulationFarmGateProcessDataById(processId);

  const { data: originalProcessData, isFetching } = useGetFarmGateProcessDataById(
    data?.originalId || null
  );

  const { data: chemicalDataType } = useGetChemicalTypes();
  const { data: wasteManagementType } = useGetWasteManagementWasteTypes();
  const { data: TransportTypeList } = useGetTransportTypesList();
  const { data: heatSourceType } = useGetHeatSourceType();
  const { data: availableHeatSourceType } = useGetFacilityHeatSources(facilityName);
  const { data: cipsipChemicalDataType } = useGetCipSipChemicalTypes();

  const chemicalData = data?.chemical || [];
  const wasteManagement = data?.wasteManagements || [];

  useEffect(() => {
    if (isFacilitySuccess) {
      setFacilityName(data?.facilityId || facilityWholeList?.[0]?.value);
      setValue("facilityName", data?.facilityId || facilityWholeList?.[0]?.value);
    }
    const checkDataTypeIsValid =
      data?.dataType === IngredientDataTypesOption.SPECIFIC ||
      data?.dataType === IngredientDataTypesOption.GENERIC;
    setCipSipData(data?.cipSip);
    setProcessingType(`${data?.processingType}`);
    setFacilityName(data?.facilityId);
    setHeatSourceTable(data?.heatSource || []);
    setDataType(
      checkDataTypeIsValid
        ? data?.dataType || IngredientDataTypesOption.SPECIFIC
        : IngredientDataTypesOption.SPECIFIC
    );
    setUnitTypes({
      capacityUnit: data?.capacityUnit ?? MEASUREMENT_TYPES.WEIGHT_KG,
      inputUnit: data?.inputUnit ?? MEASUREMENT_TYPES.WEIGHT_KG,
      outputUnit: data?.outputUnit ?? MEASUREMENT_TYPES.WEIGHT_KG,
      sideStreamUnit: data?.sideStreamUnit ?? MEASUREMENT_TYPES.WEIGHT_KG,
      heatUnit: data?.heatUnit ?? MEASUREMENT_TYPES.ENERGY_MJ_KG,
      electricityUnit: data?.electricityUnit ?? MEASUREMENT_TYPES.POWER_kWh_KG,
      waterUnit: data?.waterUnit ?? MEASUREMENT_TYPES.VOLUME_m3_PER_KG
    });
  }, [isFacilitySuccess, data]);

  useEffect(() => {
    if (availableHeatSourceType && availableHeatSourceType.length > 0 && !defaultHeatSource) {
      setDefaultHeatSource(availableHeatSourceType[0].value);
    }
  }, [availableHeatSourceType]);

  useEffect(() => {
    if (processId) {
      refetch();
    }
  }, [processId]);

  const values = useMemo(() => {
    return {
      ...data,
      capacity: data?.capacity || 0,
      input: data?.input || 0,
      output: data?.output || 0,
      sideStream: data?.sideStream || 0,
      heat: data?.heat || 0,
      electricity: data?.electricity || 0,
      water: data?.water || 0,
      citricAcid: data?.citricAcid || 0,
      heatAmount: data?.heatAmount || 0,
      facilityName: data?.facilityId
    };
  }, [nodeItem, data]);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
    control
  } = useForm({
    mode: "all",
    defaultValues: values,
    values: values
  });

  const {
    formState: { errors: errorsHeatSource },
    handleSubmit: handleSubmitHeatSource,
    setValue: setHeatValue,
    control: controlHeat
  } = useForm();

  const resetHeatResource = () => {
    setDefaultHeatSource(0);
    setHeatValue("HeatAmount", 0.0);
  };

  const handleAddHeatSource = (data: HeatSourceData) => {
    if (!handleHeatSourceValidation()) return;

    data.HeatSource = defaultHeatSource;
    data.HeatAmount = Number(data.HeatAmount);

    const existingIndex = heatSourceTable.findIndex(
      (item: any) => item.HeatSource === data.HeatSource
    );

    if (existingIndex !== -1) {
      const updatedHeatSource = heatSourceTable.map((item: any, index: number) =>
        index === existingIndex ? { ...item, HeatAmount: data.HeatAmount } : item
      );
      setHeatSourceTable(updatedHeatSource);
    } else {
      setHeatSourceTable((prev: any) => [...prev, data]);
    }
    resetHeatResource();
  };

  const handleEditHeatSource = (data: HeatSourceData) => {
    setDefaultHeatSource(data.HeatSource);
    setHeatValue("HeatAmount", data.HeatAmount);
  };

  const handleDeleteHeatSource = (data: HeatSourceData) => {
    const updatedHeatSourceTable = heatSourceTable.filter(
      (item: any) => item.HeatSource !== data.HeatSource
    );
    setHeatSourceTable(updatedHeatSourceTable);
  };

  const handleAccordion = (activeStatus: boolean, requestType: AccordionTypes) => {
    if (!activeStatus) {
      setAccordionStatus(false);
      setAccordionType(null);
      return false;
    }
    if (validateAccordionOpen(processId, accordionType, requestType)) {
      setAccordionStatus(true);
      setAccordionType(requestType);
      return true;
    }
    return false;
  };

  const handleFormSubmit = (data: ProcessFormData) => {
    if (!validateAccordionSubmit(accordionType)) {
      return;
    }
    onSubmit(data);
  };

  const updateDrawingState = (result: any) => {
    const updatedNode = {
      ...nodeItem,
      data: {
        ...nodeItem?.data,
        label: result?.processingName,
        // reference is mongoose mix type so you can set any type of here please set necessary reference only
        reference: {
          processId: result?._id
        }
      }
    };
    const updatedChartNodes = chartNodes.map((n: any) => {
      if (n.id === nodeItem?.id) {
        return updatedNode;
      }
      return n;
    });
    setChartNodes(updatedChartNodes);
    setNodeItem(updatedNode);
    saveDrawing(chartEdges, updatedChartNodes);
  };

  const updateFarmGateProcessData = (processData: any, checkIsValidCipSip: any) => {
    processData.farmGateId = processId;
    putFarmGateProcessData(processData, {
      onSuccess: (result: any) => {
        removeNewDrawingItemStatus();
        if (checkIsValidCipSip) {
          successMessage("Process updated CIP / SIP successfully");
          return refetch();
        }
        refetch();
        updateDrawingState(result);
        successMessage("Process updated successfully");
      }
    });
  };

  const createFarmGateProcessData = (processData: any) => {
    processData.farmGateId = farmGateId;
    postFarmGateProcessData(processData, {
      onSuccess: (result: any) => {
        setProcessId(result?._id);
        saveNewDrawingItem("created");
        updateDrawingState(result);
        successMessage("Process created successfully");
      }
    });
  };

  const onSubmit = (data: any, cipSip?: any) => {
    let checkIsValidCipSip = isArray(cipSip);
    let processData = {
      processingName: data?.processingName,
      ingredientId: ingredientId,
      supplierId: supplierId,
      processNumber: 1,
      facilityId: data?.facilityName,
      processingIngredient: [],
      cipSip: checkIsValidCipSip ? cipSip : cipSipData,
      processingType: data?.processingType,
      machineModelNo: data?.machineModelNo,
      capacity: data?.capacity || 0,

      input: data?.input || 0,

      output: data?.output || 0,
      sideStream: data?.sideStream || 0,
      heat: data?.heat || 0,
      electricity: data?.electricity || 0,
      water: data?.water || 0,
      citricAcid: data?.citricAcid,
      processingStatus: "ACTIVE",
      heatSource: heatSourceTable,
      changeStatus: true,
      farmGateId: farmGateId,
      processId: processId,
      dataType: dataType,
      ...unitTypes
    };
    if (processId) {
      updateFarmGateProcessData(processData, checkIsValidCipSip);
      return;
    }
    createFarmGateProcessData(processData);
  };

  const updateCipSipData = (data: any) => {
    setCipSipData(data);
    const currentData = getValues();
    onSubmit(currentData, data);
  };

  const onNodesDelete = () => {
    deleteElements({ nodes: [{ id: nodeItem.id }] });
    setNodeItem(null);
  };

  const deleteFarmGate = () => {
    onNodesDelete();
    successMessage("Process deleted successfully");
  };

  const loadCIPData = (currentData: any[]) => {
    if (nodeItem?.data.reference && currentData?.length > 0) {
      const responseSipData = Object.keys(currentData?.[0]);
      const newSipContent: any = SipContents?.map(sip => {
        const sipContent = responseSipData?.map(resSip => {
          return (
            sip?.typeName == resSip && {
              ...sip,
              value: currentData?.[0][resSip]
            }
          );
        });
        return sipContent.filter(value => value !== false);
      });
      return newSipContent.flat(Infinity);
    } else {
      return SipContents;
    }
  };

  const originalCipSipData = useMemo(
    () => loadCIPData(originalProcessData?.cipSip),
    [originalProcessData]
  );

  const simulationCipSipData = useMemo(() => loadCIPData(data?.cipSip), [data]);

  const checkRecipeValidation = () => {
    if (processId) return true;

    setBounceEnable(true);
    errorView("Please save the process to add recipe");
    return false;
  };

  const handleChemical = () => {
    if (processId) return true;

    setBounceEnable(true);
    errorView("Please save the process to add chemical details");
    return false;
  };

  const handleWasteManagement = () => {
    if (processId) return true;

    setBounceEnable(true);
    errorView("Please save the process to add waste management details");
    return false;
  };

  const handleCIPValidation = () => {
    if (processId) return true;

    setBounceEnable(true);
    errorView("Please save the process to add CIP/SIP details");
    return false;
  };

  const handleHeatSourceValidation = () => {
    if (processId) return true;

    setBounceEnable(true);
    errorView("Please save the process to add heat sources");
    return false;
  };

  const handleSave = () => {
    setBounceEnable(false);
    customCapacityValidation.current?.checkCustomRequired(getValues().capacity);
    customInputValidation.current?.checkCustomRequired(getValues().input);
    customOutputValidation.current?.checkCustomRequired(getValues().output);
  };

  if (
    (processId && isLoading) ||
    isCreateProcessLoading ||
    isUpdateProcessLoading ||
    isDeleteProcessLoading ||
    isFetching
  ) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <Grid>
      <Grid.Column computer={16} tablet={16} mobile={16} className="userBankDetailsMain">
        <div>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div
              style={{
                height: height * 0.9 - 100,
                overflowX: "hidden"
              }}
            >
              <SimulationProcessingDetails
                register={register}
                errors={errors}
                processTypesWholeList={processTypesWholeList}
                processingType={processingType}
                setProcessingType={setProcessingType}
                setValue={setValue}
                facilityName={facilityName}
                facilityWholeList={facilityWholeList}
                setFacilityName={setFacilityName}
                originalProcessData={originalProcessData}
                dataTypeOptions={dataTypeOptions}
                dataType={dataType}
                setDataType={setDataType}
                setVisibleFacilityDetailsModal={() => setVisibleFacilityModal(true)}
              />
              <SimulationMeasurementDetails
                control={control}
                errors={errors}
                customCapacityValidation={customCapacityValidation}
                customInputValidation={customInputValidation}
                customOutputValidation={customOutputValidation}
                addIngredientActive={addIngredientActive}
                setViewIngredient={setViewIngredient}
                data={data}
                originalProcessData={originalProcessData}
                unitTypes={unitTypes}
                setUnitTypes={setUnitTypes}
              />
              <Grid>
                <Grid.Column computer={16}>
                  <SimulationProcessHeatSource
                    heatSourceTable={heatSourceTable}
                    originalHeatSourceTable={originalProcessData?.heatSource}
                    heatSourceType={heatSourceType}
                    availableHeatSourceType={availableHeatSourceType}
                    defaultHeatType={defaultHeatSource}
                    setDefaultHeatType={setDefaultHeatSource}
                    control={controlHeat}
                    errorsHeatSource={errorsHeatSource}
                    handleSubmitHeatSource={handleSubmitHeatSource}
                    onAddHeatSourceHandler={handleAddHeatSource}
                    onEditHeatSourceHandler={handleEditHeatSource}
                    onDeleteHeatSourceHandler={handleDeleteHeatSource}
                  />
                </Grid.Column>
                <Grid.Column computer={16} className="pt-0">
                  <SimulationSipContentContent
                    getSipData={(data: any) => {
                      updateCipSipData(data);
                    }}
                    processCIPData={simulationCipSipData}
                    chemicalDataType={cipsipChemicalDataType}
                    currentCipSipData={data?.cipSip}
                    originalCipSipData={originalProcessData?.cipSip}
                    originalProcessCIPData={originalCipSipData}
                    handleCIPValidation={handleCIPValidation}
                    currentAccordionStatus={handleAccordion}
                  />
                </Grid.Column>
                <Grid.Column computer={16} className="pt-0">
                  <SimulationRecipeContent
                    processId={processId}
                    nodeItem={nodeItem}
                    recipeId={data?.recipeId}
                    checkRecipeValidation={checkRecipeValidation}
                    type="farm-gate"
                    originalProcessData={originalProcessData}
                    currentAccordionStatus={handleAccordion}
                  />
                </Grid.Column>
                <Grid.Column computer={16} className="pt-0">
                  <SimulationChemicalContent
                    chemicalDataType={chemicalDataType}
                    chemicalData={chemicalData}
                    processId={processId}
                    TransportTypeList={TransportTypeList}
                    refetchChemical={refetch}
                    handleOpenValidation={handleChemical}
                    type="farm"
                    originalProcessData={originalProcessData}
                    currentAccordionStatus={handleAccordion}
                  />
                </Grid.Column>
                <Grid.Column computer={16} className="pt-0">
                  <SimulationWasteManagementContent
                    wasteManagementType={wasteManagementType}
                    wasteManagement={wasteManagement}
                    processId={processId}
                    sideStreamValue={values?.sideStream}
                    refetchWasteData={refetch}
                    handleOpenValidation={handleWasteManagement}
                    type="farm"
                    originalProcessData={originalProcessData}
                    currentAccordionStatus={handleAccordion}
                  />
                </Grid.Column>
              </Grid>
              {visibleFacilityModal && (
                <FacilitySimulationEditModal
                  visibleModal={visibleFacilityModal}
                  setVisibleModal={setVisibleFacilityModal}
                  facilityId={facilityName}
                />
              )}
            </div>
            <MainBottomButtonView
              deleteStatus={processId ? true : false}
              saveButtonStatus={true}
              saveButton={() => handleSave()}
              deleteButton={() => setVisibleDeleteModal(true)}
              saveTitle={"Save"}
              bounceEnable={bounceEnable}
              type="submit"
            />
          </form>
        </div>
      </Grid.Column>
      <AddIngredientModal
        visibleModal={viewIngredient}
        setVisibleModal={() => {
          setViewIngredient(false);
        }}
      />
      <ConfirmModal
        viewModal={visibleDeleteModal}
        closeModal={() => setVisibleDeleteModal(false)}
        cancel={() => {
          setVisibleDeleteModal(false);
        }}
        approve={() => {
          const dataToDelete = {
            farmGateId: farmGateId,
            processId: processId
          };
          deleteFarmGateProcessData(dataToDelete, {
            onSuccess: (data: any) => {
              deleteFarmGate();
            }
          });
          setVisibleDeleteModal(false);
        }}
        title={`Delete ${data?.processingName} Process`}
        subTitle={`Are you sure you want to remove the ${data?.processingName} farm gate process? Please consider you cannot recover these data after remove`}
      />
    </Grid>
  );
};
